import React, { useEffect, useState } from 'react'
import { ActivityIndicator, Image, StyleSheet } from 'react-native'

import { useRoute } from '@react-navigation/native'
import { H6, SH1, Stack, colors } from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import { i18n } from '@zet/localization'
import { IBrandListRoute } from '@zet/utils/interface'

import { useGenerateSharableLink } from '../../api/brands/hooks'
import { useApplyLink } from '../../hooks/useApplyLink'
import { fdCardBottomBannerImg, magnetIds } from '../../utils/constants'
import UserConsent from '../userConsent'

const ZET_FD_CARD_BANNER_LEAD_SOURCE = 'zetFdCardBanner'

//! Intrinsic size of the banner creative on S3
const BANNER_ASPECT_RATIO = 656 / 272

interface IProps {
  customerId: number
  customerName: string
  customerPhoneNumber: number | string
}

const FdCardBottomBanner = ({
  customerId,
  customerName,
  customerPhoneNumber,
}: IProps) => {
  const route = useRoute()
  const params = route.params as IBrandListRoute

  const [consentBottomSheetVisible, setConsentBottomSheetVisible] =
    useState(false)
  //! Height is derived from the measured width instead of the aspectRatio
  //! style, which needs CSS aspect-ratio (Chrome 88+ / iOS 15+) and collapses
  //! the banner to zero height on older mobile browsers and webviews
  const [bannerWidth, setBannerWidth] = useState(0)

  const {
    mutate: fetchShareLink,
    isLoading,
    isSuccess,
    data: shareLinkData,
  } = useGenerateSharableLink()

  const openBottomSheet = () => {
    setConsentBottomSheetVisible(true)
  }
  const closeBottomSheet = () => {
    setConsentBottomSheetVisible(false)
  }

  // Same status-code handling as the web "Apply Now" flow (WebApplyButton), but the
  // banner sends the agent straight to the returned url: openLinkWithoutModal true
  // with openJFSLinkWithModal false is the only combination useApplyLink treats as a
  // direct window.location redirect, every other path opens the share/apply modal.
  const { handleApply } = useApplyLink(
    shareLinkData,
    magnetIds.brandId,
    params?.customerName || customerName,
    customerId,
    customerPhoneNumber,
    false,
    openBottomSheet,
    closeBottomSheet,
    true,
    false,
  )

  useEffect(() => {
    if (isSuccess && shareLinkData) {
      handleApply()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, shareLinkData])

  const handleBannerPress = () => {
    fetchShareLink({
      partnerId: magnetIds.brandId,
      customerId: customerId,
      leadSource: ZET_FD_CARD_BANNER_LEAD_SOURCE,
      brandSpecificPartnerId: params?.brandSpecificPartnerId,
      variantId: magnetIds.variantId,
    })
  }

  return (
    <>
      {/* End-of-list "Other offers" divider, same curved line as SeeMoreFooter */}
      <Stack style={styles.divider}>
        <Image
          style={styles.dividerLine}
          resizeMode="stretch"
          source={require('../../assets/images/seeMoreBottomLine.png')}
        />
        <SH1 style={styles.dividerLabel}>{i18n.t('brandPage.otherOffers')}</SH1>
      </Stack>

      <H6 className="mx-3" style={styles.heading}>
        {i18n.t('brandPage.didNotFindOffer')}
      </H6>

      <Pressable
        onPress={handleBannerPress}
        disabled={isLoading}
        eventName="click_action"
        eventPayload={{
          item_name: 'fd_card_bottom_banner',
          customer_id: customerId,
          partner_id: magnetIds.brandId,
        }}
      >
        <Stack
          className="mx-3"
          style={styles.bannerWrapper}
          onLayout={({ nativeEvent }) =>
            setBannerWidth(nativeEvent?.layout?.width)
          }
        >
          <Image
            source={{ uri: fdCardBottomBannerImg }}
            style={[
              styles.banner,
              bannerWidth
                ? { height: bannerWidth / BANNER_ASPECT_RATIO }
                : null,
            ]}
            resizeMode="contain"
          />
          {isLoading ? (
            <Stack style={styles.loaderOverlay}>
              <ActivityIndicator color={colors.neutral.white100} />
            </Stack>
          ) : null}
        </Stack>
      </Pressable>
      {consentBottomSheetVisible && (
        <UserConsent
          brandId={magnetIds.brandId}
          customerId={customerId}
          customerName={params?.customerName || customerName}
          leadSource={ZET_FD_CARD_BANNER_LEAD_SOURCE}
          customerPhoneNumber={customerPhoneNumber}
          isBottomSheetVisible={consentBottomSheetVisible}
          setIsBottomSheetVisible={setConsentBottomSheetVisible}
        />
      )}
    </>
  )
}

export default FdCardBottomBanner

const styles = StyleSheet.create({
  //! Spacing mirrors the Figma footer block: 30px divider, 28px to the
  //! heading, 14px to the banner. Cards already carry a 12px bottom margin,
  //! so 40px here makes up the 52px gap the design keeps above the divider.
  divider: {
    height: 30,
    marginTop: 40,
  },
  dividerLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 6,
    height: 18,
  },
  dividerLabel: {
    position: 'absolute',
    left: 0,
    right: 0,
    //! Sits 5px above the dip in the divider line so the text can breathe
    top: -2,
    textAlign: 'center',
  },
  heading: {
    marginTop: 28,
  },
  bannerWrapper: {
    marginTop: 14,
    marginBottom: 16,
  },
  banner: {
    width: '100%',
    aspectRatio: 328 / 136,
    borderRadius: 12,
  },
  loaderOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(19, 41, 2, 0.35)',
    borderRadius: 12,
  },
})
